/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { DomainsList } from 'src/luis/verticals/domainsList/DomainList';

const messages = defineMessages({
    pageTitle: {
        id: 'luis.prebuiltDomainsPage.pageTitle',
        defaultMessage: 'Prebuilt Domains'
    },
    title: {
        id: 'luis.prebuiltDomainsPage.title',
        defaultMessage: 'Prebuilt Domains'
    },
    titleDescription: {
        id: 'luis.prebuiltDomainsPage.titleDescription',
        defaultMessage: 'Prebuilt sets of intents and entities that work together for domains or common categories of client applications.'
    },
    togglingDomainToastStart: {
        id: 'luis.prebuiltDomainsPage.togglingDomainTitle',
        defaultMessage: 'Adding/removing prebuilt domain'
    },
    togglingDomainToastSuccess: {
        id: 'luis.prebuiltDomainsPage.togglingDomainToastSuccess',
        defaultMessage: 'Prebuilt domain added/removed'
    },
    togglingDomainToastFailure: {
        id: 'luis.prebuiltDomainsPage.togglingDomainToastFailure',
        defaultMessage: 'Failed to add/remove prebuilt domain'
    }
});

/**
 * Represents a page container component for
 * the prebuilt domains.
 */
export const PrebuiltDomainsPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { culture, domainStoreHouse } = rootStore.currentLuisAppStore;
    const { items: domainStores, isLoading } = domainStoreHouse;
    const { isLoaded: isIntentsLoaded, intents } = useIntents();
    const { isLoaded: isEntitiesLoaded, entities } = useEntities();
    const isInitialized = isIntentsLoaded && isEntitiesLoaded;
    const toggleDomainProgressTracker = useProgressTracker({
        onComplete: () => {
            dispatcher.dispatch('loadIntents', { context: { forceRefresh: true } });
            dispatcher.dispatch('loadEntities', { context: { forceRefresh: true } });
            dispatcher.dispatch('loadPatterns', { context: { forceRefresh: true } });
        }
    });

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    React.useEffect(() => {
        if (isInitialized) {
            dispatcher.dispatch('loadDomains', { culture });
        }
    }, [intents, entities]);

    /**
     * Callback function to delete or add domain of the current app.
     *
     * @param domainName The domain to apply the action on
     */
    const handleCardClick = (domainName: string) => {
        dispatcher.dispatch('toggleDomain', {
            culture,
            name: domainName,
            tracker: toggleDomainProgressTracker,
            toastData: {
                running: { title: localize(messages.togglingDomainToastStart) },
                completed: { title: localize(messages.togglingDomainToastSuccess) },
                failed: { title: localize(messages.togglingDomainToastFailure) }
            }
        });
    };

    return (
        <Page size="container-fluid" pageName="PrebuiltDomainsPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.title)} description={localize(messages.titleDescription)} />

            <DomainsList
                domains={domainStores}
                isLoading={isLoading || !isInitialized}
                culture={culture}
                onCardClicked={handleCardClick}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />
        </Page>
    );
});
